/**
 * Lightweight analytics beacon — fire-and-forget event emitter.
 *
 * Never throws, never blocks UX. Uses navigator.sendBeacon when available
 * (survives page unload) and falls back to fetch with keepalive otherwise.
 *
 * Hostname guard: beacons are silently dropped on preview/localhost so
 * dev/QA traffic does NOT pollute the production analytics database or
 * Cloudflare Web Analytics. Set REACT_APP_ANALYTICS_FORCE=1 to override.
 *
 * The hostname check is INLINE (not imported) to avoid webpack chunk-loading
 * edge cases when service workers serve stale builds.
 */

const API = process.env.REACT_APP_BACKEND_URL;
const ENDPOINT = `${API}/api/analytics/event`;
const SESSION_KEY = "jns_anlx_session";

const PREVIEW_HOST_PATTERNS = [
    /\.preview\.emergentagent\.com$/i,
    /^localhost$/i,
    /^127\./,
    /^192\.168\./,
    /^10\./,
    /^0\.0\.0\.0$/,
];

let _enabledCache = null;
let _logged = false;

function isEnabled() {
    if (_enabledCache !== null) return _enabledCache;
    try {
        if (process.env.REACT_APP_ANALYTICS_FORCE === "1") {
            _enabledCache = true;
            return true;
        }
        if (typeof window === "undefined" || !window.location) {
            _enabledCache = false;
            return false;
        }
        const host = window.location.hostname || "";
        const isPreview = PREVIEW_HOST_PATTERNS.some(p => p.test(host));
        _enabledCache = !isPreview;
        if (!_enabledCache && !_logged) {
            // eslint-disable-next-line no-console
            console.info(`[analytics] disabled on hostname "${host}" (preview/local)`);
            _logged = true;
        }
        return _enabledCache;
    } catch {
        _enabledCache = false;
        return false;
    }
}

// Exported so other modules (e.g. AnalyticsBeacon component for the
// Cloudflare script gate) can reuse the same logic without duplication.
export function isAnalyticsEnabled() {
    return isEnabled();
}

function getSessionToken() {
    try {
        let t = sessionStorage.getItem(SESSION_KEY);
        if (!t) {
            t = `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
            sessionStorage.setItem(SESSION_KEY, t);
        }
        return t;
    } catch {
        return null;
    }
}

export function emit(event, payload = {}) {
    if (!API) return;
    if (!isEnabled()) return;  // silently drop on preview/localhost
    try {
        const body = JSON.stringify({
            event,
            path: payload.path || (typeof window !== "undefined" ? window.location.pathname : null),
            item_id: payload.item_id,
            query: payload.query,
            session_token: getSessionToken(),
            extra: payload.extra,
        });

        // sendBeacon survives unload. Fallback to fetch with keepalive.
        if (typeof navigator !== "undefined" && navigator.sendBeacon) {
            const blob = new Blob([body], { type: "application/json" });
            navigator.sendBeacon(ENDPOINT, blob);
            return;
        }
        fetch(ENDPOINT, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body,
            credentials: "include",
            keepalive: true,
        }).catch(() => { /* swallow */ });
    } catch {
        /* analytics must never break the app */
    }
}

export function trackPageView(path) {
    emit("page_view", { path });
}

export function trackPdpView(itemId, path) {
    emit("pdp_view", { item_id: itemId, path });
}

export function trackAddToCart(itemId, extra) {
    emit("add_to_cart", { item_id: itemId, extra });
}

export function trackCheckoutStart(extra) {
    emit("checkout_start", { extra });
}

export function trackSearch(query, resultCount) {
    emit("search_performed", { query, extra: { result_count: resultCount } });
    if (resultCount === 0) emit("search_no_results", { query });
}
